<script setup lang="ts">
import { useBookingDataStore } from "@/stores/bookingData";
import { getAvailability, getServices } from "../../api/api";
import { computed, getCurrentScope, onMounted, reactive, ref, watch } from "vue";
import { useTextStore } from "@/stores/texts";
import { formatDateGSheet, firstAvailableMonth } from "@/utils.ts/date";
import BookingCalendar from "../calendar/booking-calendar.vue";
import VenueFilter from "../calendar/venue-filter.vue";
import CalendarLegend from "../calendar/calendar-legend.vue";
import { DEFAULT_LABELS, type DayAvailability, type RateCategory } from "../calendar/types";
import { analytics } from "@/analytics";
type Filter = import("../calendar/types").VenueFilter;

const bookingData = useBookingDataStore();
const textStore = useTextStore();
const componentScope = getCurrentScope();
const emit = defineEmits<{ (e: "nextStep"): void }>();

const TEXT_infodate = ref("");
const labels = reactive({ ...DEFAULT_LABELS });
// spec §7 key → CalendarLabels field
const TEXT_KEYS: Array<[keyof typeof labels, string]> = [
  ["filterLabel", "calendar_filter_label"],
  ["filterBoth", "calendar_filter_both"],
  ["filterLasalle", "calendar_filter_lasalle"],
  ["filterSerre", "calendar_filter_serre"],
  ["legendLasalle", "calendar_legend_lasalle"],
  ["legendSerre", "calendar_legend_serre"],
  ["legendSelected", "calendar_legend_selected"],
  ["legendUnavailable", "calendar_legend_unavailable"],
  ["legendDeal", "calendar_legend_deal"],
  ["emptyMonth", "calendar_empty_month"],
  ["noteSpecial", "calendar_note_special"],
  ["noteDiscount", "calendar_note_discount"],
  ["wordTable", "calendar_word_table"],
  ["wordSalle", "calendar_word_salle"],
  ["available", "calendar_available"],
  ["full", "calendar_full"],
  ["selectionCleared", "calendar_selection_cleared"],
];

const availabilityByDate = ref(new Map<string, DayAvailability>());
const availabilityLoaded = ref(false);
const selectedDate = ref<Date>();
const venueChoice = ref<Filter>("both");
const selectionCleared = ref(false);
const initialYear = ref(new Date().getFullYear());
const initialMonth = ref(new Date().getMonth());

type VenuePrices = { table?: string; salle?: string; serre?: string };
const venuePricesByDate = ref(new Map<string, VenuePrices>());

async function loadVenuePrices(day: Date) {
  const dateStr = formatDateGSheet(day);
  if (venuePricesByDate.value.has(dateStr)) return;
  const res = await getServices(day); // httpClient never rejects
  if (!res?.success) return; // don't cache a failure — a later reselection retries
  const grab = (id: string) => res.data?.find((s: { Id: string; Prix?: string }) => s.Id === id)?.Prix;
  venuePricesByDate.value.set(dateStr, {
    table: grab("dinerlatable"),
    salle: grab("dinerlasalle"),
    serre: grab("dinerlaserre"),
  });
}

function enabledUnderFilter(dateStr: string, filter: Filter): boolean {
  const entry = availabilityByDate.value.get(dateStr);
  if (!entry) return false;
  return filter === "both" || entry.venues.includes(filter);
}

onMounted(async () => {
  const now = new Date();
  // One call — the backend returns a rolling 12-month window (availability.service.ts).
  // No venue filter: per-venue selectability is the filter's and step-type's job.
  const v = await getAvailability(now.getFullYear(), now.getMonth() + 1);
  if (v?.success && v.data) {
    for (const entry of v.data) {
      // Base days omit price/discount — normalize here (spec §6).
      availabilityByDate.value.set(entry.date, {
        price: (entry.price ?? "base") as RateCategory,
        discount: entry.discount ?? 0,
        venues: entry.venues_available ?? ["lasalle", "serre"],
      });
      bookingData.venuesByDate[entry.date] = entry.venues_available ?? ["lasalle", "serre"];
    }
  }

  // Reconciliation order (spec §6): restore → fetch → init filter → reconcile.
  if (bookingData.venue === "lasalle" || bookingData.venue === "serre") {
    venueChoice.value = bookingData.venue;
  }
  const restored = bookingData.date;
  if (restored !== undefined) {
    if (enabledUnderFilter(formatDateGSheet(restored), venueChoice.value)) {
      selectedDate.value = restored;
      void loadVenuePrices(restored);
    } else {
      // Deliberate change: a stale restored date no longer keeps Next enabled.
      selectionCleared.value = true;
    }
  }

  // Open on the selected month, else the first month with availability (#171).
  const target = selectedDate.value
    ? { year: selectedDate.value.getFullYear(), month: selectedDate.value.getMonth() }
    : firstAvailableMonth([...availabilityByDate.value.keys()]);
  if (target) {
    initialYear.value = target.year;
    initialMonth.value = target.month;
  }
  availabilityLoaded.value = true;

  // Registered after the restore seeding so restore never re-arms the
  // reservation timer; run inside the captured component scope so the
  // watcher is disposed on unmount (we are past an await here).
  componentScope?.run(() => {
    watch(selectedDate, (curr) => {
      if (curr === undefined) return;
      selectionCleared.value = false;
      const entry = availabilityByDate.value.get(formatDateGSheet(curr));
      bookingData.updateDate(curr, entry?.price ?? "base", entry?.discount ?? 0);
      analytics.track('date_selected', { mode: 'normal', venue: bookingData.venue || null });
      void loadVenuePrices(curr);
    });
  });

  TEXT_infodate.value = (await textStore.getText("infodate")) || "";
  for (const [field, key] of TEXT_KEYS) {
    labels[field] = (await textStore.getText(key)) || DEFAULT_LABELS[field];
  }
});

watch(venueChoice, (filter) => {
  if (selectedDate.value && !enabledUnderFilter(formatDateGSheet(selectedDate.value), filter)) {
    selectedDate.value = undefined; // bookingData.date is kept (spec §3)
    selectionCleared.value = true;
  }
});

const summaryText = computed(() => {
  if (selectionCleared.value) return labels.selectionCleared;
  if (!selectedDate.value) return "";
  return new Intl.DateTimeFormat("fr", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(selectedDate.value);
});

// Per-venue price line; venue availability words remain the fallback while
// prices load or when a service row is missing.
const priceLine = computed(() => {
  if (selectionCleared.value || !selectedDate.value) return "";
  const dateStr = formatDateGSheet(selectedDate.value);
  const entry = availabilityByDate.value.get(dateStr);
  if (!entry) return "";
  const prices = venuePricesByDate.value.get(dateStr);
  const parts: string[] = [];
  if (venueChoice.value !== "serre") {
    if (!entry.venues.includes("lasalle")) parts.push(`La Roue : ${labels.full}`);
    else if (prices?.table && prices?.salle) parts.push(`La Roue : ${labels.wordTable} ${prices.table} / ${labels.wordSalle} ${prices.salle}`);
    else parts.push(`La Roue : ${labels.available}`);
  }
  if (venueChoice.value !== "lasalle") {
    if (!entry.venues.includes("serre")) parts.push(`La Serre : ${labels.full}`);
    else if (prices?.serre) parts.push(`La Serre : ${prices.serre}`);
    else parts.push(`La Serre : ${labels.available}`);
  }
  return parts.join(" · ");
});

const noteLine = computed(() => {
  if (selectionCleared.value || !selectedDate.value) return "";
  const entry = availabilityByDate.value.get(formatDateGSheet(selectedDate.value));
  if (!entry) return "";
  if (entry.price === "discount" && entry.discount > 0) return labels.noteDiscount.replace("{pct}", String(Math.round(entry.discount * 100)));
  if (entry.price === "special") return labels.noteSpecial;
  return "";
});

function canGoNext() {
  return selectedDate.value !== undefined;
}

function goNext() {
  // Carry the explicit filter choice forward; 'both' leaves prior choices alone.
  if (venueChoice.value !== "both") {
    bookingData.setVenue(venueChoice.value);
  }
  emit("nextStep");
}
</script>

<template>
  <div
    class="bpa-front-tabs--panel-body __bpa-is-active eauget-booking-step-date"
    data-testid="step-date-panel"
  >
    <div class="bpa-front-default-card">
      <!---->
      <div class="bpa-front-dc--body">
        <div class="el-row">
          <div
            class="el-col el-col-24 el-col-xs-24 el-col-sm-24 el-col-md-24 el-col-lg-24 el-col-xl-24"
          >
            <div
              class="bpa-front-module-container bpa-front-module--date-and-time"
            >
              <!---->
              <div class="el-row">
                <div
                  class="el-col el-col-24 el-col-xs-24 el-col-sm-24 el-col-md-24 el-col-lg-24 el-col-xl-24"
                >
                  <div class="bpa-front-module-heading">Date</div>
                  <!---->
                </div>
              </div>
              <div class="bpa-front--dt__wrapper">
                <div class="bpa-front--dt__col calendar">
                  <div class="bpa-front--dt__calendar">
                    <template v-if="availabilityLoaded">
                      <venue-filter v-model="venueChoice" :labels="labels" />
                      <booking-calendar
                        v-model="selectedDate"
                        :availability="availabilityByDate"
                        :venue-filter="venueChoice"
                        :initial-year="initialYear"
                        :initial-month="initialMonth"
                        :labels="labels"
                      />
                      <p class="date-summary" data-testid="date-summary" aria-live="polite">
                        {{ summaryText }}<template v-if="priceLine"><br />{{ priceLine }}</template><template v-if="noteLine"><br />{{ noteLine }}</template>
                      </p>
                    </template>
                    <div v-else class="bpa-front-calendar-loading" data-testid="date-picker-loading">
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                  </div>
                </div>
                <div
                  class="bpa-front--dt__col bpa-front-dt__timeslot-col bpa-front-dt-col__is-visible"
                >
                  <div class="bpa-front--dt__time-slots">
                    <div class="calendar_description">
                      <div class="bpa-front-module-heading">Calendrier</div>
                      <calendar-legend v-if="availabilityLoaded" :venue-filter="venueChoice" :labels="labels" />
                      <br />
                      <div class="bpa-front-module-heading">Créneau horaire</div>
                      <p>A partir de 18h</p>
                      <br />
                      <p>{{ TEXT_infodate }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bpa-front-dc--footer">
        <div class="el-row">
          <div class="el-col el-col-24">
            <div class="bpa-front-tabs--foot">
              <button
                type="button"
                @click="goNext()"
                :disabled="!canGoNext()"
                :class="{ 'is-disabled': !canGoNext() }"
                class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"
                data-testid="btn-next"
              >
                <!----><!----><span>
                  Suivant :&nbsp;<strong>Type de réservation</strong>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enable-background="new 0 0 24 24"
                    viewBox="0 0 24 24"
                  >
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M14.29,5.71L14.29,5.71c-0.39,0.39-0.39,1.02,0,1.41L18.17,11H3c-0.55,0-1,0.45-1,1v0c0,0.55,0.45,1,1,1h15.18l-3.88,3.88 c-0.39,0.39-0.39,1.02,0,1.41l0,0c0.39,0.39,1.02,0.39,1.41,0l5.59-5.59c0.39-0.39,0.39-1.02,0-1.41L15.7,5.71 C15.32,5.32,14.68,5.32,14.29,5.71z"
                    ></path></svg>
                  </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bpa-front-calendar-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

.date-summary {
  min-height: 22px;
  margin: 8px 0 0;
  font-size: 14px;
}

/* The BookingPress plugin CSS (host page in prod, runtime links standalone)
   hard-sets height:430px + overflow:auto on this panel, clipping the legend
   and the infodate text. Scoped attr wins the specificity contest. */
.bpa-front--dt__time-slots {
  height: auto;
  min-height: 430px;
  overflow: visible;
}

/* Same plugin fixes .bpa-front--dt__calendar at 430px (410px on mobile); the
   three-line price summary then overflows onto the legend below. */
.bpa-front--dt__calendar {
  height: auto;
}

@media only screen and (max-width: 576px) {
  .bpa-front-module-heading, p {
    margin: 0px 15px !important;
  }
}

@media (max-width: 576px) {
  .bpa-front-module--date-and-time {
    display: block !important;
  }


  div.bpa-front--dt__col.calendar {
    margin: 0px;
    padding: 0px !important;
  }

  .bpa-front-module--date-and-time .bpa-front--dt__wrapper {
    grid-template-columns: repeat(auto-fit,minmax(340px,1fr));
  }
}
</style>
